import axios from "axios";
import CryptoJS from "crypto-js";
import { createNote, uploadImageAsNote, getNoteAttachment } from "./noteService";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const MODULE_NAME = "bank_bankdetails";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";
const ENCRYPTION_KEY = "MoonaConsultancySecretKey";

// Module fields that we work with
const BANK_MODULE_FIELDS = [
  "id",
  "name",
  "parentid_c",
  "accountholdername",
  "accountnumber",
  "ifsc",
  "accounttype",
  "bankname",
  "branch",
  "bankcountry",
  "status",
  "description",
  "image",
  "date_entered",
  "date_modified",
  "deleted",
];

// UI field aliases (if needed for consistency)
const UI_FIELD_ALIASES = {
  bankdescription: "description",
};

const REVERSE_UI_FIELD_ALIASES = {
  description: "bankdescription",
};

// Fields that need encryption/decryption
const ENCRYPTED_FIELDS = ["accountnumber", "ifsc"];

/**
 * Filter outgoing payload to include only module fields used in the UI
 * and encrypt sensitive fields
 */
const filterBankPayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
    }

    // Check if field needs alias mapping
    const mappedField = UI_FIELD_ALIASES[key] || key;

    if (BANK_MODULE_FIELDS.includes(mappedField)) {
      if (
        mappedField === "image" &&
        typeof normalizedValue === "string" &&
        normalizedValue.startsWith("data:")
      ) {
        return;
      }
      // Encrypt sensitive fields before sending to API
      if (ENCRYPTED_FIELDS.includes(mappedField) && normalizedValue) {
        payload[mappedField] = encryptData(normalizedValue.toString());
      } else {
        payload[mappedField] = normalizedValue;
      }
    }
  });

  return payload;
};

/**
 * Process bank details data entries and decrypt sensitive fields
 */
const processBankData = async (entryList, sessionId, filterFn) => {
  if (!Array.isArray(entryList)) return [];

  const processedEntries = entryList
    .map((entry) => {
      if (!entry || !entry.name_value_list) return null;

      const values = {};
      Object.entries(entry.name_value_list).forEach(([key, item]) => {
        values[key] = item?.value ?? "";
      });

      const filteredValues = {};
      BANK_MODULE_FIELDS.forEach((field) => {
        if (values[field] !== undefined && values[field] !== null) {
          if (ENCRYPTED_FIELDS.includes(field) && values[field]) {
            filteredValues[field] = decryptData(values[field]);
          } else {
            filteredValues[field] = values[field];
          }
        }
      });

      Object.entries(REVERSE_UI_FIELD_ALIASES).forEach(([moduleField, uiField]) => {
        if (filteredValues[moduleField] !== undefined) {
          filteredValues[uiField] = filteredValues[moduleField];
        }
      });

      const entryData = {
        id: entry.id,
        ...filteredValues,
      };

      if (filterFn && !filterFn(entryData)) {
        return null;
      }

      return entryData;
    })
    .filter(Boolean);

  if (sessionId) {
    for (const entry of processedEntries) {
      if (entry.image && typeof entry.image === "string" && !entry.image.startsWith("data:")) {
        try {
          const imageData = await getNoteAttachment(sessionId, entry.image);
          if (imageData) {
            entry.image = imageData;
          }
        } catch (error) {
          console.error("Error fetching passbook image note attachment:", error);
        }
      }
    }
  }

  return processedEntries;
};

/**
 * Encrypt sensitive data
 */
const encryptData = (text) => {
  if (!text || text.trim() === "") return "";
  try {
    return CryptoJS.AES.encrypt(text, ENCRYPTION_KEY).toString();
  } catch (error) {
    return text;
  }
};

/**
 * Decrypt sensitive data
 */
const decryptData = (encryptedText) => {
  if (!encryptedText || encryptedText.trim() === "") return "";
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return decrypted || encryptedText;
  } catch (error) {
    return encryptedText;
  }
};

const uploadBankPassbookImage = async (sessionId, bankDetailId, base64Image) => {
  if (!sessionId || !bankDetailId || !base64Image || !base64Image.startsWith("data:")) {
    return null;
  }

  try {
    const noteName = "Bank Passbook Image";
    const noteId = await createNote(sessionId, MODULE_NAME, bankDetailId, noteName);
    const filename = `bank_passbook_${Date.now()}.jpg`;
    await uploadImageAsNote(sessionId, noteId, base64Image, filename, MODULE_NAME, bankDetailId);
    return noteId;
  } catch (error) {
    console.error("Error uploading bank passbook image:", error);
    throw error;
  }
};

/**
 * Fetch all bank details data
 */
export const fetchBankDetailsData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      select_fields: BANK_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    
    if (response?.data?.name === "Invalid Session ID" || response?.data?.number) {
      return [];
    }
    
    if (response?.data?.description && response.data.description.includes("does not exist")) {
      return [];
    }
    
  const entryList = response?.data?.entry_list || [];

  return await processBankData(entryList, sessionId, () => true);
  } catch (error) {
    if (error.response?.status === 500) {
      return [];
    }
    return [];
  }
};

/**
 * Fetch bank details by parent ID (contact ID)
 */
export const fetchBankDetailsByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) return [];

  try {
    // First, try to fetch all entries (like the working Postman request)
    const rest_data = {
      session: sessionId,
      module_name: MODULE_NAME,
    };

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(JSON.stringify(rest_data))}`;
    const response = await axios.get(fullUrl);
    
    if (response?.data?.name === "Invalid Session ID" || response?.data?.number) {
      return [];
    }
    
    if (response?.data?.description && response.data.description.includes("does not exist")) {
      return [];
    }
    
    const entryList = response?.data?.entry_list || [];
    const processedData = await processBankData(
      entryList,
      sessionId,
      (entry) => entry.parentid_c === parentId || entry.parentid_c === String(parentId)
    );
    
    return processedData;
  } catch (error) {
    if (error.response?.status === 500) {
      return [];
    }
    return [];
  }
};

/**
 * Add new bank details entry
 */
export const addBankDetailsData = async (sessionId, data) => {
  try {

    const { image, ...dataWithoutImage } = data || {};
    const filteredPayload = filterBankPayload(dataWithoutImage);

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: filteredPayload,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const createdId = response?.data?.id;

    if (createdId && image && typeof image === "string" && image.startsWith("data:")) {
      const noteId = await uploadBankPassbookImage(sessionId, createdId, image);
      if (noteId) {
        const updatePayload = filterBankPayload({ ...dataWithoutImage, id: createdId, image: noteId });
        updatePayload.id = createdId;
        updatePayload.image = noteId;

        const update_rest_data = JSON.stringify({
          session: sessionId,
          module_name: MODULE_NAME,
          name_value_list: updatePayload,
        });

        const updateUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(update_rest_data)}`;
        await axios.get(updateUrl);
      }
    }

    return response.data;
  } catch (error) {
    if (error.response?.status === 500) {
      throw new Error("Bank Details module not found in backend. Please contact administrator.");
    }
    throw error;
  }
};

/**
 * Edit existing bank details entry
 */
export const editBankDetailsData = async (sessionId, id, data) => {
  try {
    const dataWithId = { ...data, id };
    const updatedData = { ...dataWithId };

    if (data && typeof data.image === "string" && data.image.startsWith("data:")) {
      const noteId = await uploadBankPassbookImage(sessionId, id, data.image);
      updatedData.image = noteId;
    }

    const filteredPayload = filterBankPayload(updatedData);
    filteredPayload.id = id;

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: filteredPayload,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Soft delete bank details entry
 */
export const deleteBankDetailsData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: {
        id,
        deleted: "1",
      },
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sync bank details entries - handles add/edit/delete operations
 */
export const syncBankDetailsEntries = async (
  sessionId,
  parentId,
  entries,
  removedIds = []
) => {
  try {
    for (const id of removedIds) {
      if (id && id.trim() !== "") {
        await deleteBankDetailsData(sessionId, id);
      }
    }

    for (const entry of entries) {
      const bankData = {
        accountholdername: entry.accountholdername || "",
        accountnumber: entry.accountnumber || "",
        ifsc: entry.ifsc || "",
        accounttype: entry.accounttype || "",
        bankname: entry.bankname || "",
        branch: entry.branch || "",
        bankcountry: entry.bankcountry || "",
        status: entry.status || "Active",
        bankdescription: entry.bankdescription || "",
        parentid_c: parentId,
      };

      if (entry.image !== undefined) {
        bankData.image = entry.image;
      }

      const hasCoreRequiredFields =
        bankData.accountholdername && bankData.accountholdername.trim().length > 0 &&
        bankData.accountnumber && bankData.accountnumber.trim().length > 0 &&
        bankData.ifsc && bankData.ifsc.trim().length > 0 &&
        bankData.bankname && bankData.bankname.trim().length > 0 &&
        bankData.branch && bankData.branch.trim().length > 0;

      if (hasCoreRequiredFields) {
        if (entry.id && entry.id.trim() !== "") {
          await editBankDetailsData(sessionId, entry.id, bankData);
        } else {
          await addBankDetailsData(sessionId, bankData);
        }
      }
    }

    return { success: true };
  } catch (error) {
    throw error;
  }
};

export default {
  fetchBankDetailsData,
  fetchBankDetailsByParent,
  addBankDetailsData,
  editBankDetailsData,
  deleteBankDetailsData,
  syncBankDetailsEntries,
};
